/**
 * Shared shell for the public policy pages (Privacy, Terms).
 *
 * Both pages are long-form legal text a reviewer needs to navigate, so they get
 * the same treatment: a hero matching the About page, a sticky table of contents
 * built from the section list, and prose styled by public.css. Sections are
 * passed as data so the contents list and the body can never drift apart.
 */

import React from 'react';
import { CalendarClock } from 'lucide-react';
import { PublicPageLayout, LAST_UPDATED, COMPANY_NAME, CONTACT_EMAIL } from './PublicPageLayout';
import './public.css';

export interface PolicySection {
  id: string;
  heading: string;
  body: React.ReactNode;
}

interface PolicyPageProps {
  eyebrow: string;
  title: string;
  intro: string;
  sections: PolicySection[];
}

export function PolicyPage({ eyebrow, title, intro, sections }: PolicyPageProps) {
  return (
    <PublicPageLayout width="full">
      <div className="ac">
        <header className="ac-policy-hero">
          <div className="ac-shell">
            <span className="ac-eyebrow">{eyebrow}</span>
            <h1>{title}</h1>
            <p>{intro}</p>
            <span className="ac-stamp">
              <CalendarClock size={15} />
              Last updated {LAST_UPDATED}
            </span>
          </div>
        </header>

        <div className="ac-shell ac-policy">
          <nav className="ac-toc" aria-label="Contents">
            <span>Contents</span>
            <ol>
              {sections.map((s) => (
                <li key={s.id}>
                  <a href={`#${s.id}`}>{s.heading}</a>
                </li>
              ))}
            </ol>
          </nav>

          <div className="ac-prose">
            {sections.map((s) => (
              <section key={s.id} id={s.id}>
                <h2>{s.heading}</h2>
                {s.body}
              </section>
            ))}

            <div className="ac-policy-foot">
              <p>Questions about anything on this page?</p>
              <p>
                {COMPANY_NAME} — <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>
              </p>
            </div>
          </div>
        </div>
      </div>
    </PublicPageLayout>
  );
}
